import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  MapPin,
  Building2,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Star,
  Trophy,
  Search,
  LayoutDashboard,
  ListChecks,
  FileText,
  ExternalLink,
  BookOpen,
  CheckCircle,
  Eye,
  Info,
  PhoneCall,
  Mail,
  Sparkles,
  Code,
  Shield,
  Save,
  X,
  Download,
  Upload,
  UploadCloud,
  FileSpreadsheet,
  RefreshCw,
} from "lucide-react";
import { STATES, stateByCode, getStateName, getStateUrl, getCityUrl, slugifyCity, LISTINGS } from "../../constants";
import {
  me,
  getCourses,
  getContent,
  createCourse,
  updateCourse,
  deleteCourse,
  deleteCoursesBatch,
  deleteAllCourses,
  deleteAllBlogs,
  deleteAllContactMessages,
  clearAllData,
  updateContent,
  getBlogs,
  createBlog,
  updateBlog,
  deleteBlog,
  getAllCityContents,
  saveCityContent,
  deleteCityContent,
  getAllStateContents,
  saveStateContent,
  deleteStateContent,
  getAboutContent,
  getContactContent,
  getContactMessages,
  getPrivacyContent,
  savePrivacyContent,
  getTermsContent,
  saveTermsContent,
  getSeoSettings,
  saveSeoSettings,
  getLocalCourses,
  saveLocalCourses,
  getLocalBlogs,
  saveLocalBlogs,
  getLocalContent,
  getLocalCityContents,
  saveLocalCityContents,
  getLocalStateContents,
  saveLocalStateContents,
  getLocalAboutContent,
  getLocalContactContent,
  getLocalContactMessages,
  getLocalPrivacyContent,
  getLocalTermsContent,
  getLocalSeoSettings,
  saveLocalSeoSettings,
  bulkImportCourses,
  bulkImportCityContents,
  bulkImportStateContents,
  bulkImportBlogs,
  bulkImportContactMessages,
  apiError,
} from "../../api";
import CsvUploadModal from "../../components/admin/CsvUploadModal";
import {
  parseUniversalFile,
  toCsv,
  downloadCsv,
  downloadExcel,
  mapRowHeaders,
  CSV_SECTIONS,
} from "../../utils/csvHelper";
import CourseForm from "./CourseForm";
import BlogForm from "./BlogForm";
import CityContentForm from "./CityContentForm";
import StateContentForm from "./StateContentForm";
import AboutContentEditor from "./AboutContentEditor";
import ContactContentEditor from "./ContactContentEditor";
import ContactMessagesInbox from "./ContactMessagesInbox";
import PolicyContentEditor from "./PolicyContentEditor";
import SchemaSettingsEditor from "./SchemaSettingsEditor";
import SEO from "../../components/SEO";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState("overview");
  const [courses, setCourses] = useState(() => getLocalCourses());
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvModalSection, setCsvModalSection] = useState("courses");
  const [blogs, setBlogs] = useState(() => getLocalBlogs());
  const [content, setContent] = useState(() => getLocalContent());
  const [cityContents, setCityContents] = useState(() => getLocalCityContents());
  const [stateContents, setStateContents] = useState(() => getLocalStateContents());
  const [stateOrCityView, setStateOrCityView] = useState("cities"); // default to cities
  const [stateFilter, setStateFilter] = useState("");
  const [stateStatusFilter, setStateStatusFilter] = useState("all");
  const [editingState, setEditingState] = useState(null);
  const [aboutContent, setAboutContent] = useState(() => getLocalAboutContent());
  const [contactContent, setContactContent] = useState(() => getLocalContactContent());
  const [contactMessages, setContactMessages] = useState(() => getLocalContactMessages());
  const [privacyContent, setPrivacyContent] = useState(() => getLocalPrivacyContent());
  const [termsContent, setTermsContent] = useState(() => getLocalTermsContent());
  const [seoSettings, setSeoSettings] = useState(() => getLocalSeoSettings());
  const [filter, setFilter] = useState("");
  const [blogFilter, setBlogFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [cityStatusFilter, setCityStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [editingCity, setEditingCity] = useState(null);
  const [showNewCityModal, setShowNewCityModal] = useState(false);
  const [newCityState, setNewCityState] = useState("al");
  const [newCityName, setNewCityName] = useState("");

  const logout = useCallback(() => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/admin");
      return;
    }
    me()
      .then(() =>
        Promise.allSettled([
          getCourses(),
          getContent(),
          getBlogs({ all: true }),
          getAllCityContents(),
          getAllStateContents(),
          getAboutContent(),
          getContactContent(),
          getContactMessages(),
          getPrivacyContent(),
          getTermsContent(),
          getSeoSettings(),
        ])
      )
      .then(([csRes, ctRes, blRes, ccRes, stRes, abRes, coRes, msRes, prRes, trRes, seRes]) => {
        if (csRes.status === "fulfilled" && csRes.value) setCourses(csRes.value);
        if (ctRes.status === "fulfilled" && ctRes.value) setContent(ctRes.value);
        if (blRes.status === "fulfilled" && blRes.value) setBlogs(blRes.value);
        if (ccRes.status === "fulfilled" && ccRes.value) setCityContents(ccRes.value);
        if (stRes.status === "fulfilled" && stRes.value) setStateContents(stRes.value);
        if (abRes.status === "fulfilled" && abRes.value) setAboutContent(abRes.value);
        if (coRes.status === "fulfilled" && coRes.value) setContactContent(coRes.value);
        if (msRes.status === "fulfilled" && msRes.value) setContactMessages(msRes.value);
        if (prRes.status === "fulfilled" && prRes.value) setPrivacyContent(prRes.value);
        if (trRes.status === "fulfilled" && trRes.value) setTermsContent(trRes.value);
        if (seRes.status === "fulfilled" && seRes.value) setSeoSettings(seRes.value);
      })
      .catch((err) => {
        console.error("Admin authentication check failed:", err);
        logout();
      });
  }, [navigate, logout]);

  const fail = (err) => toast({ title: "Error", description: apiError(err), variant: "destructive" });

  const handleSave = async (data) => {
    try {
      if (editing === "new") {
        const course = await createCourse(data);
        setCourses([course, ...courses]);
        toast({ title: "Course added", description: `${course.name} has been created.` });
      } else {
        const course = await updateCourse(editing.id, data);
        setCourses(courses.map((c) => (c.id === course.id ? course : c)));
        toast({ title: "Course updated", description: `${course.name} has been saved.` });
      }
      setEditing(null);
    } catch (err) {
      fail(err);
    }
  };

  const handleDelete = async (course) => {
    if (!window.confirm(`Delete "${course.name}"? This cannot be undone.`)) return;
    try {
      await deleteCourse(course.id);
      setCourses((prev) => prev.filter((c) => c.id !== course.id));
      setSelectedCourseIds((prev) => prev.filter((id) => id !== course.id));
      toast({ title: "Course deleted", description: `${course.name} has been removed.` });
    } catch (err) {
      fail(err);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedCourseIds.length === 0) return;
    const count = selectedCourseIds.length;
    if (
      !window.confirm(
        `Are you sure you want to delete ${count} selected course${count > 1 ? "s" : ""}? This action cannot be undone.`
      )
    ) {
      return;
    }
    setIsBulkDeleting(true);
    try {
      await deleteCoursesBatch(selectedCourseIds);
      setCourses((prev) => prev.filter((c) => !selectedCourseIds.includes(c.id)));
      setSelectedCourseIds([]);
      toast({
        title: "Courses deleted",
        description: `Successfully removed ${count} course${count > 1 ? "s" : ""}.`,
      });
    } catch (err) {
      fail(err);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleDeleteAllCourses = async () => {
    if (courses.length === 0) return;
    const count = courses.length;
    if (
      !window.confirm(
        `⚠️ DANGER WARNING: Are you sure you want to DELETE ALL ${count} course listings? This will completely clear all courses and cannot be undone!`
      )
    ) {
      return;
    }
    setIsBulkDeleting(true);
    try {
      await deleteAllCourses();
      setCourses([]);
      setSelectedCourseIds([]);
      toast({
        title: "All courses deleted",
        description: `Removed all ${count} course listings from the directory.`,
      });
    } catch (err) {
      fail(err);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleClearAllDummyData = async () => {
    if (
      !window.confirm(
        "⚠️ RESET DATABASE & CLEAR ALL DUMMY DATA:\n\nAre you sure you want to completely wipe all courses, sample blog posts, and contact inquiries from both the database and local storage?\n\nThis will reset your directory to 0 listings so you can start completely fresh with your own imports."
      )
    ) {
      return;
    }
    setIsBulkDeleting(true);
    try {
      await clearAllData();
      setCourses([]);
      setSelectedCourseIds([]);
      setBlogs([]);
      setContactMessages([]);
      toast({
        title: "Database & Dummy Data Cleared!",
        description: "All sample courses, blogs, and inquiries have been cleared. Directory is now clean and empty.",
      });
    } catch (err) {
      fail(err);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleDeleteAllBlogs = async () => {
    if (blogs.length === 0) return;
    if (!window.confirm(`⚠️ Are you sure you want to delete ALL ${blogs.length} blog posts? This cannot be undone.`)) return;
    try {
      await deleteAllBlogs();
      setBlogs([]);
      toast({ title: "All Articles Deleted", description: "All blog posts have been removed." });
    } catch (err) {
      fail(err);
    }
  };

  const toggleFeatured = async (course) => {
    try {
      const updated = await updateCourse(course.id, { ...course, featured: !course.featured });
      setCourses(courses.map((c) => (c.id === course.id ? updated : c)));
    } catch (err) {
      fail(err);
    }
  };

  const handleSaveBlog = async (data) => {
    try {
      if (editingBlog === "new") {
        const newPost = await createBlog(data);
        setBlogs([newPost, ...blogs]);
        toast({ title: "Article created", description: `"${newPost.title}" has been published.` });
      } else {
        const updated = await updateBlog(editingBlog.id, data);
        setBlogs(blogs.map((b) => (b.id === updated.id ? updated : b)));
        toast({ title: "Article updated", description: `"${updated.title}" has been saved.` });
      }
      setEditingBlog(null);
    } catch (err) {
      fail(err);
    }
  };

  const handleDeleteBlog = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await deleteBlog(post.id);
      setBlogs(blogs.filter((b) => b.id !== post.id));
      toast({ title: "Article deleted", description: `"${post.title}" has been removed.` });
    } catch (err) {
      fail(err);
    }
  };

  const toggleBlogPublished = async (post) => {
    try {
      const updated = await updateBlog(post.id, { ...post, published: !post.published });
      setBlogs(blogs.map((b) => (b.id === post.id ? updated : b)));
      toast({
        title: updated.published ? "Article published" : "Article unpublished",
        description: `"${post.title}" is now ${updated.published ? "visible" : "hidden"}.`,
      });
    } catch (err) {
      fail(err);
    }
  };

  const saveContent = async () => {
    try {
      setContent(await updateContent(content));
      toast({ title: "Content saved", description: "Homepage content has been updated." });
    } catch (err) {
      fail(err);
    }
  };

  const handleSaveCityContent = async (data) => {
    try {
      const saved = await saveCityContent(data.state, data.citySlug, data);
      const s = data.state.toLowerCase();
      const c = data.citySlug.toLowerCase();
      const idx = cityContents.findIndex(
        (item) => item.state.toLowerCase() === s && item.citySlug.toLowerCase() === c
      );
      if (idx >= 0) {
        setCityContents(cityContents.map((item, i) => (i === idx ? saved : item)));
      } else {
        setCityContents([saved, ...cityContents]);
      }
      toast({
        title: "City Content Saved",
        description: `Content for ${data.cityName}, ${getStateName(data.state)} has been updated.`,
      });
      setEditingCity(null);
    } catch (err) {
      fail(err);
    }
  };

  const handleResetCityContent = async (stateCode, citySlug) => {
    try {
      await deleteCityContent(stateCode, citySlug);
      const s = stateCode.toLowerCase();
      const c = citySlug.toLowerCase();
      setCityContents(
        cityContents.filter(
          (item) => !(item.state.toLowerCase() === s && item.citySlug.toLowerCase() === c)
        )
      );
      toast({
        title: "City Content Reset",
        description: "Reverted back to dynamic auto-generated content.",
      });
      setEditingCity(null);
    } catch (err) {
      fail(err);
    }
  };

  const handleCreateNewCity = (e) => {
    e.preventDefault();
    if (!newCityName.trim()) return;
    const slug = slugifyCity(newCityName.trim());
    const stateCode = newCityState.toLowerCase();
    const existingCourses = courses.filter(
      (c) => (c.state || "").toLowerCase() === stateCode && (c.citySlug || slugifyCity(c.city || "")).toLowerCase() === slug
    );
    const existingContent = cityContents.find(
      (cc) => cc.state.toLowerCase() === stateCode && cc.citySlug.toLowerCase() === slug
    );

    setEditingCity({
      city: {
        state: stateCode,
        citySlug: slug,
        cityName: newCityName.trim(),
        courses: existingCourses,
      },
      currentContent: existingContent || null,
    });
    setShowNewCityModal(false);
    setNewCityName("");
  };

  const handleSaveSeoSettings = async (data) => {
    try {
      const saved = await saveSeoSettings(data);
      setSeoSettings(saved);
      toast({
        title: "Schema & SEO Saved",
        description: "Structured data and SEO settings updated successfully.",
      });
    } catch (err) {
      fail(err);
    }
  };

  const handleSavePrivacyContent = async (data) => {
    try {
      const saved = await savePrivacyContent(data);
      setPrivacyContent(saved);
      toast({
        title: "Privacy Policy Saved",
        description: "Privacy Policy content updated successfully.",
      });
    } catch (err) {
      fail(err);
    }
  };

  const handleSaveTermsContent = async (data) => {
    try {
      const saved = await saveTermsContent(data);
      setTermsContent(saved);
      toast({
        title: "Terms & Conditions Saved",
        description: "Terms & Conditions content updated successfully.",
      });
    } catch (err) {
      fail(err);
    }
  };

  const handleEditCity = (city) => {
    const current = cityContents.find(
      (cc) => cc.state.toLowerCase() === city.state.toLowerCase() && cc.citySlug.toLowerCase() === city.citySlug.toLowerCase()
    );
    setEditingCity({
      city,
      currentContent: current || null,
    });
  };

  const handleSaveStateContent = async (data) => {
    try {
      const saved = await saveStateContent(data.stateCode, data);
      const exists = stateContents.some((s) => s.stateCode.toLowerCase() === data.stateCode.toLowerCase());
      if (exists) {
        setStateContents(stateContents.map((s) => (s.stateCode.toLowerCase() === data.stateCode.toLowerCase() ? saved : s)));
      } else {
        setStateContents([saved, ...stateContents]);
      }
      toast({
        title: "State Content Saved",
        description: `Custom content for ${data.stateName || data.stateCode.toUpperCase()} saved.`,
      });
      setEditingState(null);
    } catch (err) {
      fail(err);
    }
  };

  const handleResetStateContent = async (stateCode) => {
    try {
      await deleteStateContent(stateCode);
      setStateContents(stateContents.filter((s) => s.stateCode.toLowerCase() !== stateCode.toLowerCase()));
      toast({
        title: "State Content Reset",
        description: "Reverted back to dynamic auto-generated content.",
      });
      setEditingState(null);
    } catch (err) {
      fail(err);
    }
  };

  const handleEditState = (st) => {
    const current = stateContents.find((sc) => sc.stateCode.toLowerCase() === st.code.toLowerCase());
    const stateCourses = courses.filter((c) => (c.state || "").toLowerCase() === st.code.toLowerCase());
    setEditingState({
      state: st,
      currentContent: current || null,
      courses: stateCourses,
    });
  };

  // Group courses by city
  const citiesMap = React.useMemo(() => {
    const map = {};
    courses.forEach((c) => {
      const code = (c.state || "").toLowerCase();
      const slug = c.citySlug || slugifyCity(c.city || "");
      const key = `${code}:${slug}`;
      if (!map[key]) {
        map[key] = {
          state: code,
          citySlug: slug,
          cityName: c.city,
          courses: [],
        };
      }
      map[key].courses.push(c);
    });

    // Also include all directory listings from listingsData
    (LISTINGS || []).forEach((l) => {
      const code = (l.stateCode || "").toLowerCase();
      const slug = (l.citySlug || "").toLowerCase();
      const key = `${code}:${slug}`;
      if (!map[key]) {
        map[key] = {
          state: code,
          citySlug: slug,
          cityName: l.cityName,
          path: l.path,
          courses: [],
        };
      } else if (!map[key].path && l.path) {
        map[key].path = l.path;
      }
    });

    // Also include any cities that have custom city content
    cityContents.forEach((cc) => {
      const code = (cc.state || "").toLowerCase();
      const slug = (cc.citySlug || "").toLowerCase();
      const key = `${code}:${slug}`;
      if (!map[key]) {
        map[key] = {
          state: code,
          citySlug: slug,
          cityName: cc.cityName || slug.replace(/-/g, " "),
          courses: [],
        };
      }
    });

    return map;
  }, [courses, cityContents]);

  const allCitiesList = React.useMemo(() => {
    return Object.values(citiesMap).sort((a, b) => {
      if (b.courses.length !== a.courses.length) {
        return b.courses.length - a.courses.length;
      }
      return a.cityName.localeCompare(b.cityName);
    });
  }, [citiesMap]);

  const handleExportDatabase = () => {
    try {
      const fullData = {
        exportedAt: new Date().toISOString(),
        totalCourses: courses.length,
        totalCities: allCitiesList.length,
        courses: courses,
        cityContents: cityContents,
        stateContents: stateContents,
        blogs: blogs,
        content: content,
        seoSettings: seoSettings,
      };
      const blob = new Blob([JSON.stringify(fullData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `minigolf_database_backup_${courses.length}_courses.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({
        title: "Database Exported Successfully!",
        description: `Exported ${courses.length} courses and ${allCitiesList.length} cities to JSON backup.`,
      });
    } catch (err) {
      toast({ title: "Export Failed", description: String(err), variant: "destructive" });
    }
  };

  const handleImportDatabase = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fileName = file.name.toLowerCase();
      // If it's a JSON file, check if it's full database backup or course list
      if (fileName.endsWith(".json")) {
        const text = await file.text();
        const json = JSON.parse(text);
        let importedCount = 0;
        let importedCourses = null;
        if (Array.isArray(json)) {
          importedCourses = json;
        } else if (json.courses && Array.isArray(json.courses)) {
          importedCourses = json.courses;
        }

        if (importedCourses && importedCourses.length > 0) {
          const updated = await bulkImportCourses(importedCourses, "append");
          setCourses(updated);
          importedCount = importedCourses.length;
        }
        if (json.cityContents && Array.isArray(json.cityContents)) {
          await bulkImportCityContents(json.cityContents, "append");
          setCityContents(json.cityContents);
        }
        if (json.stateContents && Array.isArray(json.stateContents)) {
          await bulkImportStateContents(json.stateContents, "append");
          setStateContents(json.stateContents);
        }
        if (json.blogs && Array.isArray(json.blogs)) {
          await bulkImportBlogs(json.blogs, "append");
          setBlogs(json.blogs);
        }
        if (json.seoSettings) {
          await saveSeoSettings(json.seoSettings);
          setSeoSettings(json.seoSettings);
        }
        toast({
          title: "Import Successful!",
          description: `Loaded ${importedCount} courses and database content into website.`,
        });
      } else {
        // Excel (.xlsx, .xls), CSV (.csv), TSV (.tsv)
        const rows = await parseUniversalFile(file);
        if (!rows || rows.length === 0) {
          toast({
            title: "Empty File",
            description: "No data rows were found in the uploaded file.",
            variant: "destructive",
          });
          return;
        }

        const schema = CSV_SECTIONS.courses;
        const normalized = rows.map((row) => {
          const mapped = mapRowHeaders(row, schema.aliases);
          Object.keys(row).forEach((k) => {
            if (mapped[k] === undefined) mapped[k] = row[k];
          });
          return schema.normalize ? schema.normalize(mapped) : mapped;
        });

        const validCourses = normalized.filter((c) => {
          const errs = schema.validate ? schema.validate(c) : [];
          return errs.length === 0;
        });

        if (validCourses.length === 0) {
          toast({
            title: "No Valid Records",
            description: "Could not find valid course listings in the uploaded file.",
            variant: "destructive",
          });
          return;
        }

        const updated = await bulkImportCourses(validCourses, "append");
        setCourses(updated);
        toast({
          title: "Import Successful!",
          description: `Imported ${validCourses.length} courses from ${file.name}.`,
        });
      }
    } catch (err) {
      console.error("Import error:", err);
      toast({
        title: "Import Error",
        description: err.message || "Failed to parse and import file.",
        variant: "destructive",
      });
    } finally {
      e.target.value = "";
    }
  };

  const handleOpenCsvModal = (secKey) => {
    setCsvModalSection(secKey);
    setCsvModalOpen(true);
  };

  const handleImportCoursesCsv = async (validItems, rawRows, mode) => {
    const updated = await bulkImportCourses(validItems, mode);
    setCourses(updated);
    toast({
      title: "Courses Imported",
      description: `Successfully imported ${validItems.length} courses into directory.`,
    });
  };

  const handleExportCoursesCsv = () => {
    const cols = CSV_SECTIONS.courses.columns;
    const rows = courses.map((c) => ({
      name: c.name,
      city: c.city,
      state: c.state,
      address: c.address || "",
      phone: c.phone || "",
      website: c.website || "",
      priceRange: c.priceRange || "",
      rating: c.rating || 4.5,
      reviewCount: c.reviewCount || 0,
      featured: c.featured ? "true" : "false",
      description: c.description || "",
      image: c.image || "",
      seoTitle: c.seoTitle || "",
      seoDescription: c.seoDescription || "",
      hours_monday: c.hours?.Monday || "",
      hours_tuesday: c.hours?.Tuesday || "",
      hours_wednesday: c.hours?.Wednesday || "",
      hours_thursday: c.hours?.Thursday || "",
      hours_friday: c.hours?.Friday || "",
      hours_saturday: c.hours?.Saturday || "",
      hours_sunday: c.hours?.Sunday || "",
    }));
    const csv = toCsv(rows, cols);
    downloadCsv(`minigolf_courses_${courses.length}_listings.csv`, csv);
    toast({
      title: "Courses Exported",
      description: `Exported ${courses.length} courses to CSV.`,
    });
  };

  const handleExportCoursesExcel = () => {
    const cols = CSV_SECTIONS.courses.columns;
    const rows = courses.map((c) => ({
      name: c.name,
      city: c.city,
      state: c.state,
      address: c.address || "",
      phone: c.phone || "",
      website: c.website || "",
      priceRange: c.priceRange || "",
      rating: c.rating || 4.5,
      reviewCount: c.reviewCount || 0,
      featured: c.featured ? "true" : "false",
      description: c.description || "",
      image: c.image || "",
      seoTitle: c.seoTitle || "",
      seoDescription: c.seoDescription || "",
      hours_monday: c.hours?.Monday || "",
      hours_tuesday: c.hours?.Tuesday || "",
      hours_wednesday: c.hours?.Wednesday || "",
      hours_thursday: c.hours?.Thursday || "",
      hours_friday: c.hours?.Friday || "",
      hours_saturday: c.hours?.Saturday || "",
      hours_sunday: c.hours?.Sunday || "",
    }));
    downloadExcel(`minigolf_courses_${courses.length}_listings.xlsx`, rows, cols, "Courses");
    toast({
      title: "Courses Exported to Excel",
      description: `Exported ${courses.length} courses to .xlsx.`,
    });
  };

  const handleImportStatesCsv = async (validItems, rawRows, mode) => {
    const updated = await bulkImportStateContents(validItems, mode);
    setStateContents(updated);
    toast({
      title: "State Content Imported",
      description: `Successfully imported custom content for ${validItems.length} states.`,
    });
  };

  const handleExportStatesCsv = () => {
    const cols = CSV_SECTIONS.states.columns;
    const rows = STATES.map(([code, name]) => {
      const custom = stateContents.find((sc) => sc.stateCode.toLowerCase() === code.toLowerCase());
      return {
        stateCode: code,
        stateName: name,
        customTitle: custom?.customTitle || "",
        leadParagraph: custom?.leadParagraph || "",
        contentParagraphs: (custom?.contentParagraphs || []).join(" | "),
      };
    });
    const csv = toCsv(rows, cols);
    downloadCsv(`states_custom_content_${STATES.length}_states.csv`, csv);
    toast({
      title: "States Exported",
      description: `Exported state pages configuration to CSV.`,
    });
  };

  const handleExportStatesExcel = () => {
    const cols = CSV_SECTIONS.states.columns;
    const rows = STATES.map(([code, name]) => {
      const custom = stateContents.find((sc) => sc.stateCode.toLowerCase() === code.toLowerCase());
      return {
        stateCode: code,
        stateName: name,
        customTitle: custom?.customTitle || "",
        leadParagraph: custom?.leadParagraph || "",
        contentParagraphs: (custom?.contentParagraphs || []).join(" | "),
      };
    });
    downloadExcel(`states_custom_content_${STATES.length}_states.xlsx`, rows, cols, "States");
    toast({
      title: "States Exported to Excel",
      description: `Exported ${STATES.length} state pages to .xlsx.`,
    });
  };

  const handleImportCitiesCsv = async (validItems, rawRows, mode) => {
    const updated = await bulkImportCityContents(validItems, mode);
    setCityContents(updated);
    toast({
      title: "City Content Imported",
      description: `Successfully imported custom content for ${validItems.length} cities.`,
    });
  };

  const handleExportCitiesCsv = () => {
    const cols = CSV_SECTIONS.cities.columns;
    const rows = allCitiesList.map((c) => {
      const custom = cityContents.find(
        (cc) => cc.state.toLowerCase() === c.state.toLowerCase() && cc.citySlug.toLowerCase() === c.citySlug.toLowerCase()
      );
      return {
        state: c.state,
        cityName: c.cityName,
        citySlug: c.citySlug,
        customTitle: custom?.customTitle || "",
        leadParagraph: custom?.leadParagraph || "",
        contentParagraphs: (custom?.contentParagraphs || []).join(" | "),
      };
    });
    const csv = toCsv(rows, cols);
    downloadCsv(`cities_custom_content_${allCitiesList.length}_cities.csv`, csv);
    toast({
      title: "Cities Exported",
      description: `Exported ${allCitiesList.length} city page records to CSV.`,
    });
  };

  const handleExportCitiesExcel = () => {
    const cols = CSV_SECTIONS.cities.columns;
    const rows = allCitiesList.map((c) => {
      const custom = cityContents.find(
        (cc) => cc.state.toLowerCase() === c.state.toLowerCase() && cc.citySlug.toLowerCase() === c.citySlug.toLowerCase()
      );
      return {
        state: c.state,
        cityName: c.cityName,
        citySlug: c.citySlug,
        customTitle: custom?.customTitle || "",
        leadParagraph: custom?.leadParagraph || "",
        contentParagraphs: (custom?.contentParagraphs || []).join(" | "),
      };
    });
    downloadExcel(`cities_custom_content_${allCitiesList.length}_cities.xlsx`, rows, cols, "Cities");
    toast({
      title: "Cities Exported to Excel",
      description: `Exported ${allCitiesList.length} city page records to .xlsx.`,
    });
  };

  const handleImportBlogsCsv = async (validItems, rawRows, mode) => {
    const updated = await bulkImportBlogs(validItems, mode);
    setBlogs(updated);
    toast({
      title: "Blogs Imported",
      description: `Successfully imported ${validItems.length} blog posts.`,
    });
  };

  const handleExportBlogsCsv = () => {
    const cols = CSV_SECTIONS.blogs.columns;
    const rows = blogs.map((b) => ({
      title: b.title,
      slug: b.slug,
      category: b.category,
      author: b.author,
      readTime: b.readTime,
      coverImage: b.coverImage,
      excerpt: b.excerpt,
      content: b.content,
      published: b.published ? "true" : "false",
      publishedAt: b.publishedAt,
    }));
    const csv = toCsv(rows, cols);
    downloadCsv(`blog_articles_${blogs.length}_posts.csv`, csv);
    toast({
      title: "Blogs Exported",
      description: `Exported ${blogs.length} articles to CSV.`,
    });
  };

  const handleExportBlogsExcel = () => {
    const cols = CSV_SECTIONS.blogs.columns;
    const rows = blogs.map((b) => ({
      title: b.title,
      slug: b.slug,
      category: b.category,
      author: b.author,
      readTime: b.readTime,
      coverImage: b.coverImage,
      excerpt: b.excerpt,
      content: b.content,
      published: b.published ? "true" : "false",
      publishedAt: b.publishedAt,
    }));
    downloadExcel(`blog_articles_${blogs.length}_posts.xlsx`, rows, cols, "Blogs");
    toast({
      title: "Blogs Exported to Excel",
      description: `Exported ${blogs.length} articles to .xlsx.`,
    });
  };

  const handleImportContentCsv = async (validItems, rawRows, mode) => {
    const updates = {};
    validItems.forEach((item) => {
      if (item.field) updates[item.field] = item.value;
    });
    const merged = { ...content, ...updates };
    try {
      await updateContent(merged);
      setContent(merged);
      saveLocalContent(merged);
      toast({
        title: "Site Content Imported",
        description: `Imported ${Object.keys(updates).length} content fields from CSV.`,
      });
    } catch (e) {
      toast({ title: "Import Error", description: String(e), variant: "destructive" });
    }
  };

  const handleExportContentCsv = () => {
    const rows = Object.entries(content || {})
      .filter(([k, v]) => typeof v === "string")
      .map(([field, value]) => ({ field, value }));
    const cols = [
      { key: "field", label: "Content Field Key" },
      { key: "value", label: "Value / Text" },
    ];
    const csv = toCsv(rows, cols);
    downloadCsv("homepage_content.csv", csv);
    toast({
      title: "Site Content Exported",
      description: `Exported homepage content keys to CSV.`,
    });
  };

  const handleUniversalImport = async (validItems, rawRows, mode) => {
    if (csvModalSection === "courses") {
      await handleImportCoursesCsv(validItems, rawRows, mode);
    } else if (csvModalSection === "states") {
      await handleImportStatesCsv(validItems, rawRows, mode);
    } else if (csvModalSection === "cities") {
      await handleImportCitiesCsv(validItems, rawRows, mode);
    } else if (csvModalSection === "blogs") {
      await handleImportBlogsCsv(validItems, rawRows, mode);
    } else if (csvModalSection === "content") {
      await handleImportContentCsv(validItems, rawRows, mode);
    } else if (csvModalSection === "messages") {
      const updated = await bulkImportContactMessages(validItems, mode);
      setContactMessages(updated);
    }
  };

  const filteredCities = React.useMemo(() => {
    const q = cityFilter.trim().toLowerCase();
    return allCitiesList.filter((city) => {
      const stName = getStateName(city.state).toLowerCase();
      const matchesSearch =
        !q ||
        city.cityName.toLowerCase().includes(q) ||
        city.citySlug.includes(q) ||
        stName.includes(q) ||
        city.state.includes(q);

      if (!matchesSearch) return false;

      const hasCustom = cityContents.some(
        (cc) => cc.state.toLowerCase() === city.state && cc.citySlug.toLowerCase() === city.citySlug
      );

      if (cityStatusFilter === "custom") return hasCustom;
      if (cityStatusFilter === "auto") return !hasCustom;
      return true;
    });
  }, [allCitiesList, cityFilter, cityStatusFilter, cityContents]);

  const filtered = courses.filter(
    (c) =>
      c.name.toLowerCase().includes(filter.toLowerCase()) ||
      c.city.toLowerCase().includes(filter.toLowerCase())
  );

  const allFilteredIds = React.useMemo(() => filtered.map((c) => c.id), [filtered]);
  const selectedFilteredCount = allFilteredIds.filter((id) => selectedCourseIds.includes(id)).length;
  const isAllSelected = filtered.length > 0 && selectedFilteredCount === filtered.length;
  const isSomeSelected = selectedFilteredCount > 0 && selectedFilteredCount < filtered.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedCourseIds((prev) => prev.filter((id) => !allFilteredIds.includes(id)));
    } else {
      setSelectedCourseIds((prev) => Array.from(new Set([...prev, ...allFilteredIds])));
    }
  };

  const toggleSelectCourse = (id) => {
    setSelectedCourseIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const featuredCount = courses.filter((c) => c.featured).length;
  const avgRating = courses.length
    ? (courses.reduce((a, c) => a + Number(c.rating || 0), 0) / courses.length).toFixed(2)
    : 0;

  const customizedCitiesCount = cityContents.length;

  const allStatesList = React.useMemo(() => {
    return STATES.map((st) => {
      const stateCourses = courses.filter((c) => (c.state || "").toLowerCase() === st.code.toLowerCase());
      const stateListings = (LISTINGS || []).filter((l) => (l.stateCode || "").toLowerCase() === st.code.toLowerCase());
      const stateCities = [
        ...new Set([
          ...stateCourses.map((c) => c.city).filter(Boolean),
          ...stateListings.map((l) => l.cityName).filter(Boolean),
        ]),
      ];
      const custom = stateContents.find((sc) => sc.stateCode.toLowerCase() === st.code.toLowerCase());
      return {
        ...st,
        courses: stateCourses,
        cities: stateCities,
        isCustom: Boolean(custom),
        content: custom || null,
      };
    });
  }, [courses, stateContents]);

  const customizedStatesCount = React.useMemo(
    () => allStatesList.filter((s) => s.isCustom).length,
    [allStatesList]
  );

  const filteredStates = React.useMemo(() => {
    const q = stateFilter.trim().toLowerCase();
    return allStatesList.filter((s) => {
      const matchesSearch =
        !q ||
        s.name.toLowerCase().includes(q) ||
        s.code.toLowerCase().includes(q);

      if (!matchesSearch) return false;

      if (stateStatusFilter === "custom") return s.isCustom;
      if (stateStatusFilter === "auto") return !s.isCustom;
      return true;
    });
  }, [allStatesList, stateFilter, stateStatusFilter]);

  const unreadMessagesCount = contactMessages.filter((m) => !m.isRead).length;

  const inputCls =
    "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500";

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard | Mini Golf USA" description="Admin dashboard to manage golf courses, state content, and blogs." noindex={true} />
      <Toaster />
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-6 h-6 text-emerald-500" />
            <span className="text-lg font-extrabold text-gray-900">Mini Golf USA</span>
            <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <button
              onClick={handleExportDatabase}
              title="Export all courses, cities, and database content as JSON"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 text-xs font-bold transition-colors shadow-xs"
            >
              <Download className="w-3.5 h-3.5" /> Export Data ({courses.length})
            </button>
            <button
              type="button"
              onClick={() => handleOpenCsvModal("courses")}
              title="Import Excel (.xlsx, .xls), CSV, TSV, or JSON data"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 text-xs font-bold transition-colors shadow-xs cursor-pointer"
            >
              <UploadCloud className="w-3.5 h-3.5" /> Import Data (Excel / CSV / JSON)
            </button>
            <Link
              to="/courses"
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-emerald-600 transition-colors"
            >
              <ListChecks className="w-3.5 h-3.5" /> All Courses Directory
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-emerald-600 transition-colors"
            >
              <ExternalLink className="w-4 h-4" /> View Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { id: "overview", label: "Overview", icon: LayoutDashboard },
            { id: "courses", label: "Courses", icon: ListChecks },
            { id: "states", label: "State Pages", icon: MapPin },
            { id: "cities", label: "City Pages", icon: Building2 },
            { id: "content", label: "Site Content", icon: FileText },
            { id: "policies", label: "Legal & Policies", icon: Shield },
            { id: "about", label: "About Us", icon: Info },
            { id: "contact", label: "Contact Us", icon: PhoneCall },
            {
              id: "messages",
              label: "Inquiries",
              icon: Mail,
              badge: unreadMessagesCount,
            },
            { id: "blogs", label: "Blog & Articles", icon: BookOpen },
            { id: "schema", label: "Schema & SEO", icon: Code },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === t.id
                  ? "bg-[#e5243b] text-white shadow-sm"
                  : "bg-white text-gray-700 border border-gray-200 hover:border-red-300"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
              {t.badge > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-400 text-gray-900">
                  {t.badge}
                </span>
              )}
              {t.id === "cities" && customizedCitiesCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white">
                  {customizedCitiesCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight mb-6">Dashboard Overview</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <ListChecks className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="text-sm text-gray-500">Total Courses</div>
                </div>
                <div className="text-3xl font-extrabold text-gray-900 mt-3" data-testid="stat-total-courses">{courses.length}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-sm text-gray-500">Total Cities</div>
                </div>
                <div className="text-3xl font-extrabold text-gray-900 mt-3">{allCitiesList.length}</div>
                <div className="text-xs text-gray-500 mt-1">{customizedCitiesCount} custom pages configured</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#e5243b]" />
                  </div>
                  <div className="text-sm text-gray-500">Customer Inquiries</div>
                </div>
                <div className="text-3xl font-extrabold text-gray-900 mt-3">{contactMessages.length}</div>
                <div className="text-xs text-[#e5243b] font-semibold mt-1">{unreadMessagesCount} unread messages</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-amber-500" />
                  </div>
                  <div className="text-sm text-gray-500">Blog Articles</div>
                </div>
                <div className="text-3xl font-extrabold text-gray-900 mt-3">{blogs.length}</div>
              </div>
            </div>

            {/* Database & Website Directory Status Card */}
            <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-xs">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3 ${
                    courses.length > 0
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-blue-50 text-blue-700 border border-blue-200"
                  }`}>
                    <CheckCircle className={`w-3.5 h-3.5 ${courses.length > 0 ? "text-emerald-600" : "text-blue-600"}`} />
                    {courses.length > 0
                      ? `Public Directory Live: All ${courses.length} Courses Visible`
                      : "Directory Empty & Clean: Ready for Your Data"}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
                    Website Data Synchronization
                  </h2>
                  <p className="text-sm text-gray-600 mt-2 max-w-2xl leading-relaxed">
                    {courses.length > 0 ? (
                      <>
                        Your public website now displays the full directory of <strong>{courses.length} courses</strong> and <strong>{allCitiesList.length} cities</strong> ({customizedCitiesCount} custom configured pages). Visitors can search, filter by state, and browse the full directory on <Link to="/courses" className="text-[#e5243b] font-bold underline">/courses</Link>.
                      </>
                    ) : (
                      <>
                        All dummy courses and sample data have been cleared. The database is clean and ready. You can import your real course listings using the Excel, CSV, or JSON import buttons below.
                      </>
                    )}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {courses.length > 0 && (
                    <button
                      onClick={handleExportDatabase}
                      className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md transition-all flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" /> Export All Data ({courses.length})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleOpenCsvModal("courses")}
                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <UploadCloud className="w-4 h-4" /> Import Data (Excel / CSV / JSON)
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAllDummyData}
                    disabled={isBulkDeleting}
                    title="Clear dummy courses, blogs, and inquiries to start fresh"
                    className="px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" /> Clear Database & Dummy Data
                  </button>
                  <Link
                    to="/courses"
                    className="px-5 py-2.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-bold text-xs shadow-md transition-all flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" /> Open /courses
                  </Link>
                </div>
              </div>
            </div>

            {/* Central Spreadsheet & Bulk Operations Hub Card */}
            <div className="mt-6 bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl border border-slate-700/50">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-white/10">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 mb-3">
                    <UploadCloud className="w-3.5 h-3.5" /> Universal Data Engine (Excel, CSV, TSV, JSON)
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white">
                    Spreadsheet & Bulk Operations Hub
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-300 mt-1.5 max-w-2xl leading-relaxed">
                    Import and export directory data, state copy, city landing pages, blog posts, inquiries, and site content using Excel (.xlsx, .xls), CSV, TSV, or JSON files.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => handleOpenCsvModal("courses")}
                    className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-gray-950 font-bold text-xs shadow-md transition-all flex items-center gap-1.5"
                  >
                    <UploadCloud className="w-4 h-4" /> Import Data (Excel / CSV)
                  </button>
                  <button
                    type="button"
                    onClick={handleExportCoursesExcel}
                    className="px-4 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-xs shadow-md transition-all flex items-center gap-1.5"
                  >
                    <FileSpreadsheet className="w-4 h-4" /> Export Excel (.xlsx)
                  </button>
                  <button
                    type="button"
                    onClick={handleExportCoursesCsv}
                    className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs border border-white/20 transition-all flex items-center gap-1.5"
                  >
                    <Download className="w-4 h-4" /> Export CSV
                  </button>
                </div>
              </div>

              {/* Section quick actions grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mt-6">
                {[
                  { id: "courses", label: "Courses Directory", desc: "Bulk import venues, ratings, hours & contacts", count: `${courses.length} courses` },
                  { id: "states", label: "State Pages Copy", desc: "Hero headlines & paragraphs for 50 US states", count: `${customizedStatesCount} custom states` },
                  { id: "cities", label: "City Pages Content", desc: "Customized city guides & SEO highlights", count: `${customizedCitiesCount} custom cities` },
                  { id: "blogs", label: "Blog Articles", desc: "Articles, guides, previews & markdown text", count: `${blogs.length} articles` },
                  { id: "content", label: "Homepage Site Copy", desc: "Hero titles, spotlight banners & footer text", count: "Homepage copy" },
                  { id: "messages", label: "Customer Inquiries", desc: "Import historical forms or contact entries", count: `${contactMessages.length} messages` },
                ].map((item) => (
                  <div key={item.id} className="p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-emerald-500/50 transition-all flex flex-col justify-between gap-3">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-sm text-white">{item.label}</span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                          {item.count}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{item.desc}</p>
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                      <button
                        type="button"
                        onClick={() => handleOpenCsvModal(item.id)}
                        className="flex-1 py-1.5 px-2.5 rounded-lg bg-white/10 hover:bg-emerald-600 text-white text-xs font-semibold transition-colors flex items-center justify-center gap-1"
                      >
                        <UploadCloud className="w-3 h-3" /> Import (Excel / CSV)
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const sc = CSV_SECTIONS[item.id];
                          if (sc) {
                            const csvStr = toCsv(sc.sampleRows, sc.columns);
                            downloadCsv(sc.filename, csvStr);
                          }
                        }}
                        title="Download sample template"
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/15 text-slate-300 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "courses" && (
          <div>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight" data-testid="manage-courses-heading">
                  Manage Courses ({filtered.length})
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Select courses using checkboxes to delete in bulk or manage individual listings.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Search courses..."
                    data-testid="admin-course-search"
                    className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {courses.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    data-testid="select-all-courses-btn"
                    className="px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {isAllSelected ? "Deselect All" : `Select All (${filtered.length})`}
                  </button>
                )}

                {selectedCourseIds.length > 0 && (
                  <button
                    type="button"
                    onClick={handleDeleteSelected}
                    disabled={isBulkDeleting}
                    data-testid="batch-delete-courses-btn"
                    className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {isBulkDeleting ? "Deleting..." : `Delete Selected (${selectedCourseIds.length})`}
                  </button>
                )}

                {courses.length > 0 && (
                  <button
                    type="button"
                    onClick={handleDeleteAllCourses}
                    disabled={isBulkDeleting}
                    data-testid="delete-all-courses-btn"
                    className="flex items-center gap-1 px-3 py-2 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete All
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleExportCoursesExcel}
                  title="Export courses to Excel (.xlsx)"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-lg hover:bg-emerald-100 transition-colors shadow-sm"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Export Excel
                </button>

                <button
                  type="button"
                  onClick={handleExportCoursesCsv}
                  title="Export courses to CSV"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenCsvModal("courses")}
                  title="Import courses from Excel, CSV, TSV, or JSON"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <UploadCloud className="w-3.5 h-3.5" /> Import Data (Excel / CSV)
                </button>

                <button
                  onClick={() => setEditing("new")}
                  data-testid="add-course-button"
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" /> Add Course
                </button>
              </div>
            </div>

            {/* Selection Status Banner */}
            {selectedCourseIds.length > 0 && (
              <div className="mb-4 px-4 py-3 bg-red-50/90 border border-red-200 rounded-xl flex flex-wrap items-center justify-between gap-3 animate-in fade-in duration-150">
                <div className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#e5243b] text-white font-bold text-xs">
                    {selectedCourseIds.length}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {selectedCourseIds.length} course{selectedCourseIds.length > 1 ? "s" : ""} selected
                  </span>
                  {filtered.length > selectedCourseIds.length && (
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedCourseIds(Array.from(new Set([...selectedCourseIds, ...allFilteredIds])))
                      }
                      className="text-xs text-[#e5243b] hover:underline font-semibold"
                    >
                      Select all {filtered.length} matching courses
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedCourseIds([])}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Clear Selection
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteSelected}
                    disabled={isBulkDeleting}
                    className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-white bg-[#e5243b] rounded-lg hover:bg-[#c9182d] shadow-sm transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {isBulkDeleting ? "Deleting..." : `Delete Selected (${selectedCourseIds.length})`}
                  </button>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-courses-table">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
                      <th className="w-12 px-4 py-3.5">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            ref={(el) => {
                              if (el) el.indeterminate = isSomeSelected;
                            }}
                            checked={isAllSelected}
                            onChange={toggleSelectAll}
                            aria-label="Select all courses"
                            data-testid="select-all-courses-checkbox"
                            className="w-4 h-4 rounded border-gray-300 text-[#e5243b] focus:ring-[#e5243b] cursor-pointer"
                          />
                        </div>
                      </th>
                      <th className="px-5 py-3.5 font-semibold">Course</th>
                      <th className="px-5 py-3.5 font-semibold">Location</th>
                      <th className="px-5 py-3.5 font-semibold">Rating</th>
                      <th className="px-5 py-3.5 font-semibold">Reviews</th>
                      <th className="px-5 py-3.5 font-semibold">Featured</th>
                      <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((c) => {
                      const isSelected = selectedCourseIds.includes(c.id);
                      return (
                        <tr
                          key={c.id}
                          className={`transition-colors ${
                            isSelected ? "bg-red-50/40 hover:bg-red-50/60" : "hover:bg-gray-50"
                          }`}
                          data-testid={`course-row-${c.id}`}
                        >
                          <td className="w-12 px-4 py-3">
                            <div className="flex items-center justify-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelectCourse(c.id)}
                                aria-label={`Select ${c.name}`}
                                data-testid={`select-course-checkbox-${c.id}`}
                                className="w-4 h-4 rounded border-gray-300 text-[#e5243b] focus:ring-[#e5243b] cursor-pointer"
                              />
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <img
                                src={c.image}
                                alt={c.name}
                                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                              />
                              <span className="font-semibold text-gray-900">{c.name}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-gray-600">
                            {c.city}, {stateByCode(c.state)?.name || c.state}
                          </td>
                          <td className="px-5 py-3">
                            <span className="inline-flex items-center gap-1">
                              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                              {c.rating}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-gray-600">{c.reviewCount}</td>
                          <td className="px-5 py-3">
                            <button
                              onClick={() => toggleFeatured(c)}
                              data-testid={`toggle-featured-${c.id}`}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                                c.featured
                                  ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                              }`}
                            >
                              {c.featured ? "Featured" : "Standard"}
                            </button>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <div className="inline-flex gap-1">
                              <button
                                onClick={() => setEditing(c)}
                                data-testid={`edit-course-${c.id}`}
                                className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                aria-label="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(c)}
                                data-testid={`delete-course-${c.id}`}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                aria-label="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-5 py-12 text-center text-gray-500">
                          <div className="max-w-md mx-auto space-y-3">
                            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                              <CheckCircle className="w-6 h-6" />
                            </div>
                            <h4 className="text-base font-bold text-gray-800">
                              {courses.length === 0 ? "Database is Clean & Ready" : "No courses match your search"}
                            </h4>
                            <p className="text-xs text-gray-500">
                              {courses.length === 0
                                ? "All dummy courses have been cleared. You can now import your listings via Excel (.xlsx), CSV, or JSON."
                                : "Try clearing your search query or state filter."}
                            </p>
                            {courses.length === 0 && (
                              <div className="pt-2 flex items-center justify-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleOpenCsvModal("courses")}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#e5243b] hover:bg-[#cf1c31] text-white text-xs font-bold rounded-lg shadow-sm transition-all cursor-pointer"
                                >
                                  <UploadCloud className="w-4 h-4" /> Import Listings
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditing("new")}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold rounded-lg shadow-sm transition-all cursor-pointer"
                                >
                                  <Plus className="w-4 h-4" /> Add Course Manually
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "states" && (
          <div>
            {/* Header: State Pages Content */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                  State Pages Content ({allStatesList.length})
                </h1>
                <p className="text-xs text-gray-500 mt-1">
                  Customize hero titles, lead paragraphs, and SEO content for all 50 US State landing pages
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleExportStatesExcel}
                  title="Export state page content to Excel (.xlsx)"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-lg hover:bg-emerald-100 transition-colors shadow-sm"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Export Excel
                </button>
                <button
                  type="button"
                  onClick={handleExportStatesCsv}
                  title="Export state page content to CSV"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
                <button
                  type="button"
                  onClick={() => handleOpenCsvModal("states")}
                  title="Import state page content from Excel, CSV, TSV, or JSON"
                  className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                >
                  <UploadCloud className="w-3.5 h-3.5" /> Import (Excel / CSV)
                </button>
                <button
                  onClick={() => setTab("cities")}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors flex items-center gap-1.5"
                >
                  <Building2 className="w-4 h-4 text-emerald-600" /> Switch to City Pages ({allCitiesList.length})
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex gap-3 flex-wrap items-center justify-between bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex gap-3 flex-wrap items-center flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={stateFilter}
                      onChange={(e) => setStateFilter(e.target.value)}
                      placeholder="Search states (e.g. Alabama, California, NV)..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <select
                    value={stateStatusFilter}
                    onChange={(e) => setStateStatusFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium text-gray-700"
                  >
                    <option value="all">All States ({allStatesList.length})</option>
                    <option value="custom">Custom Content ({customizedStatesCount})</option>
                    <option value="auto">Dynamic Auto-Generated ({allStatesList.length - customizedStatesCount})</option>
                  </select>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
                        <th className="px-5 py-3.5 font-semibold">State</th>
                        <th className="px-5 py-3.5 font-semibold">Courses</th>
                        <th className="px-5 py-3.5 font-semibold">Cities</th>
                        <th className="px-5 py-3.5 font-semibold">Page Content Status</th>
                        <th className="px-5 py-3.5 font-semibold">Live URL</th>
                        <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredStates.map((st) => {
                        const isCustom = st.isCustom;
                        return (
                          <tr key={st.code} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs uppercase flex-shrink-0 ${
                                  st.code === "al" ? "bg-red-500 text-white" : "bg-red-50 text-[#e5243b]"
                                }`}>
                                  {st.code}
                                </div>
                                <div>
                                  <span className="font-bold text-gray-900 block text-sm">
                                    {st.name} {st.code === "al" && "⭐"}
                                  </span>
                                  <span className="text-xs text-gray-500 uppercase">
                                    {st.code}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                                {st.courses.length} {st.courses.length === 1 ? "course" : "courses"}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <span className="text-xs text-gray-600 font-medium">
                                {st.cities.length} {st.cities.length === 1 ? "city" : "cities"}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              {isCustom ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                  <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> Custom Editorial Content
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                                  <Sparkles className="w-3.5 h-3.5 text-blue-500" /> Dynamic Auto-Generated
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-4">
                              <a
                                href={getStateUrl(st.code)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 font-medium"
                              >
                                <span>{getStateUrl(st.code)}</span>
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </td>
                            <td className="px-5 py-4 text-right">
                              <div className="inline-flex gap-2 items-center justify-end">
                                <button
                                  onClick={() => handleEditState(st)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-lg transition-colors shadow-sm"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                  {isCustom ? "Edit Content" : "Customize"}
                                </button>
                                {isCustom && (
                                  <button
                                    onClick={() => handleResetStateContent(st.code)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Reset to default auto-generated content"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredStates.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-5 py-12 text-center text-gray-400">
                            No states match your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "cities" && (
          <div>
            {/* Header: City Pages Content */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                  City Pages Content ({allCitiesList.length})
                </h1>
                <p className="text-xs text-gray-500 mt-1">
                  Customize hero titles, lead paragraphs, and narrative content for individual city landing pages
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setTab("states")}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors flex items-center gap-1.5"
                >
                  <MapPin className="w-4 h-4 text-emerald-600" /> Switch to State Pages ({allStatesList.length})
                </button>
              </div>
            </div>

            {/* ================= CITY PAGES VIEW ================= */}
            {stateOrCityView === "cities" && (
              <div className="space-y-4">
                <div className="flex gap-3 flex-wrap items-center justify-between bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex gap-3 flex-wrap items-center flex-1">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={cityFilter}
                        onChange={(e) => setCityFilter(e.target.value)}
                        placeholder="Search cities or states..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <select
                      value={cityStatusFilter}
                      onChange={(e) => setCityStatusFilter(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium text-gray-700"
                    >
                      <option value="all">All Cities ({allCitiesList.length})</option>
                      <option value="custom">Custom Content ({customizedCitiesCount})</option>
                      <option value="auto">Auto-Generated ({allCitiesList.length - customizedCitiesCount})</option>
                    </select>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleExportCitiesExcel}
                      title="Export city page content to Excel (.xlsx)"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-lg hover:bg-emerald-100 transition-colors shadow-sm"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Export Excel
                    </button>
                    <button
                      type="button"
                      onClick={handleExportCitiesCsv}
                      title="Export city page content to CSV"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                    >
                      <Download className="w-3.5 h-3.5" /> Export CSV
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOpenCsvModal("cities")}
                      title="Import city page content from Excel, CSV, TSV, or JSON"
                      className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                    >
                      <UploadCloud className="w-3.5 h-3.5" /> Import (Excel / CSV)
                    </button>
                    <button
                      onClick={() => setShowNewCityModal(true)}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors shadow-sm"
                    >
                      <Plus className="w-4 h-4" /> Add / Customize City
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
                          <th className="px-5 py-3.5 font-semibold">City & State</th>
                          <th className="px-5 py-3.5 font-semibold">Courses</th>
                          <th className="px-5 py-3.5 font-semibold">Page Content Status</th>
                          <th className="px-5 py-3.5 font-semibold">Live URL</th>
                          <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredCities.map((city) => {
                          const custom = cityContents.find(
                            (cc) => cc.state.toLowerCase() === city.state.toLowerCase() && cc.citySlug.toLowerCase() === city.citySlug.toLowerCase()
                          );
                          const isCustom = Boolean(custom);

                          return (
                            <tr key={`${city.state}-${city.citySlug}`} className="hover:bg-gray-50/80 transition-colors">
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center text-[#e5243b] font-bold text-xs uppercase flex-shrink-0">
                                    {city.state}
                                  </div>
                                  <div>
                                    <span className="font-bold text-gray-900 block text-sm">
                                      {city.cityName}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {getStateName(city.state)}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-4">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                                  {city.courses.length} {city.courses.length === 1 ? "course" : "courses"}
                                </span>
                              </td>
                              <td className="px-5 py-4">
                                {isCustom ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> Custom Editorial Content
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                                    <Sparkles className="w-3.5 h-3.5 text-blue-500" /> Dynamic Auto-Generated
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-4">
                                <a
                                  href={city.path || getCityUrl(city.state, city.citySlug)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 font-medium"
                                >
                                  <span>{city.path || getCityUrl(city.state, city.citySlug)}</span>
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </td>
                              <td className="px-5 py-4 text-right">
                                <div className="inline-flex gap-2 items-center justify-end">
                                  <button
                                    onClick={() => handleEditCity(city)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-lg transition-colors shadow-sm"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                    {isCustom ? "Edit Content" : "Customize"}
                                  </button>
                                  {isCustom && (
                                    <button
                                      onClick={() => handleResetCityContent(city.state, city.citySlug)}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Reset to default auto-generated content"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {filteredCities.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-5 py-12 text-center text-gray-400">
                              No cities match your search. Click "+ Add / Customize City" to add content for a new city.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "blogs" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                  Manage Blog Articles ({blogs.length})
                </h1>
                <p className="text-xs text-gray-500 mt-1">Create, edit, publish, and delete blog articles displayed on the website</p>
              </div>
              <div className="flex gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={blogFilter}
                    onChange={(e) => setBlogFilter(e.target.value)}
                    placeholder="Search articles..."
                    className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#e5243b]"
                  />
                </div>
                {blogs.length > 0 && (
                  <button
                    type="button"
                    onClick={handleDeleteAllBlogs}
                    title="Delete all articles"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete All
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleExportBlogsExcel}
                  title="Export articles to Excel (.xlsx)"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-lg hover:bg-emerald-100 transition-colors shadow-sm"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Export Excel
                </button>
                <button
                  type="button"
                  onClick={handleExportBlogsCsv}
                  title="Export articles to CSV"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
                <button
                  type="button"
                  onClick={() => handleOpenCsvModal("blogs")}
                  title="Import articles from Excel, CSV, TSV, or JSON"
                  className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                >
                  <UploadCloud className="w-3.5 h-3.5" /> Import (Excel / CSV)
                </button>
                <button
                  onClick={() => setEditingBlog("new")}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-lg shadow-sm transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add Blog Post
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                      <th className="px-5 py-3.5 font-semibold">Article</th>
                      <th className="px-5 py-3.5 font-semibold">Category</th>
                      <th className="px-5 py-3.5 font-semibold">Author</th>
                      <th className="px-5 py-3.5 font-semibold">Date</th>
                      <th className="px-5 py-3.5 font-semibold">Status</th>
                      <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {blogs
                      .filter((b) =>
                        !blogFilter.trim() ||
                        b.title.toLowerCase().includes(blogFilter.toLowerCase()) ||
                        b.category?.toLowerCase().includes(blogFilter.toLowerCase()) ||
                        b.author?.toLowerCase().includes(blogFilter.toLowerCase())
                      )
                      .map((post) => (
                        <tr key={post.id} className="hover:bg-gray-50/70 transition-colors">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <img
                                src={post.coverImage}
                                alt={post.title}
                                className="w-12 h-10 rounded-lg object-cover flex-shrink-0"
                              />
                              <div className="min-w-0">
                                <span className="font-bold text-gray-900 block truncate max-w-xs sm:max-w-sm">
                                  {post.title}
                                </span>
                                <span className="text-xs text-gray-400 block truncate max-w-xs">
                                  /blog/{post.slug}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                              {post.category || "General"}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-gray-600 text-xs font-medium">{post.author}</td>
                          <td className="px-5 py-3.5 text-gray-500 text-xs">{post.publishedAt}</td>
                          <td className="px-5 py-3.5">
                            <button
                              onClick={() => toggleBlogPublished(post)}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                                post.published !== false
                                  ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                              }`}
                            >
                              {post.published !== false ? "Published" : "Draft"}
                            </button>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <div className="inline-flex gap-1.5 items-center">
                              <Link
                                to={`/blog/${post.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="View live article"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Link>
                              <button
                                onClick={() => setEditingBlog(post)}
                                className="p-1.5 text-gray-400 hover:text-[#e5243b] hover:bg-red-50 rounded-lg transition-colors"
                                title="Edit article"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteBlog(post)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete article"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    {blogs.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-5 py-10 text-center text-gray-400">
                          No blog posts yet. Click "Add Blog Post" to publish your first article.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "about" && (
          <AboutContentEditor
            initialContent={aboutContent}
            onUpdate={(updated) => setAboutContent(updated)}
          />
        )}

        {tab === "contact" && (
          <ContactContentEditor
            initialContent={contactContent}
            onUpdate={(updated) => setContactContent(updated)}
          />
        )}

        {tab === "messages" && (
          <ContactMessagesInbox
            messages={contactMessages}
            onUpdate={(updated) => setContactMessages(updated)}
          />
        )}

        {tab === "content" && content && (
          <div className="space-y-8 max-w-5xl">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                  Website Content & Page Copy
                </h1>
                <p className="text-xs text-gray-500 mt-1">
                  Manage hero titles, spotlight banners, intro narratives, stats cards, directory headers, and footer branding.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleExportContentCsv}
                  title="Export site content to CSV"
                  className="flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
                <button
                  type="button"
                  onClick={() => handleOpenCsvModal("content")}
                  title="Import site content from Excel, CSV, TSV, or JSON"
                  className="flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors shadow-sm"
                >
                  <UploadCloud className="w-3.5 h-3.5" /> Import (Excel / CSV)
                </button>
                <button
                  onClick={saveContent}
                  data-testid="save-content-button"
                  className="inline-flex items-center gap-1.5 px-6 py-2.5 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-md transition-all active:scale-[0.98]"
                >
                  <Save className="w-4 h-4" /> Save Site Content
                </button>
              </div>
            </div>

            {/* 1. Homepage Hero & Search */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 shadow-sm">
              <div className="border-b border-gray-100 pb-3">
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#e5243b]"></span> 1. Homepage Hero & Header
                </h2>
                <p className="text-xs text-gray-500">The main headline, accent color line, and subtitle on the homepage.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Hero Title (Line 1)</label>
                  <input
                    type="text"
                    value={content.heroTitle1 || ""}
                    data-testid="content-hero-title1"
                    onChange={(e) => setContent({ ...content, heroTitle1: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Hero Title (Line 2, Accent Gradient)</label>
                  <input
                    type="text"
                    value={content.heroTitle2 || ""}
                    data-testid="content-hero-title2"
                    onChange={(e) => setContent({ ...content, heroTitle2: e.target.value })}
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Hero Subtitle</label>
                <textarea
                  rows={2}
                  value={content.heroSubtitle || ""}
                  data-testid="content-hero-subtitle"
                  onChange={(e) => setContent({ ...content, heroSubtitle: e.target.value })}
                  className={inputCls}
                />
              </div>
            </div>

            {/* 2. Spotlight Banner */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 shadow-sm">
              <div className="border-b border-gray-100 pb-3">
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" /> 2. Featured Spotlight Banner (Homepage)
                </h2>
                <p className="text-xs text-gray-500">Highlighted state/regional feature banner displayed below the hero.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Spotlight Headline</label>
                  <input
                    type="text"
                    value={content.spotlightTitle || "Featured Spotlight: Mini Golf in Alabama"}
                    onChange={(e) => setContent({ ...content, spotlightTitle: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Button Action Text</label>
                  <input
                    type="text"
                    value={content.spotlightButtonText || "View Alabama Courses"}
                    onChange={(e) => setContent({ ...content, spotlightButtonText: e.target.value })}
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Spotlight Description</label>
                  <textarea
                    rows={2}
                    value={content.spotlightSubtitle || "Explore popular indoor and outdoor putt putt courses from coastal greens in Gulf Shores to Birmingham glow courses."}
                    onChange={(e) => setContent({ ...content, spotlightSubtitle: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Button Target Link</label>
                  <input
                    type="text"
                    value={content.spotlightButtonLink || "/state/alabama"}
                    onChange={(e) => setContent({ ...content, spotlightButtonLink: e.target.value })}
                    className={inputCls}
                    placeholder="/state/alabama"
                  />
                </div>
              </div>
            </div>

            {/* 3. Homepage Intro SEO Section */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 shadow-sm">
              <div className="border-b border-gray-100 pb-3">
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#e5243b]" /> 3. Homepage Editorial Guide & Narrative
                </h2>
                <p className="text-xs text-gray-500">Comprehensive overview text. Hyperlinks supported: [Link Text](https://example.com)</p>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Intro Section Heading (H2)</label>
                <input
                  type="text"
                  value={content.introTitle || ""}
                  data-testid="content-intro-title"
                  onChange={(e) => setContent({ ...content, introTitle: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  Intro Paragraphs (Separated by blank line)
                </label>
                <textarea
                  rows={8}
                  value={(content.introParagraphs || []).join("\n\n")}
                  data-testid="content-intro-paragraphs"
                  onChange={(e) =>
                    setContent({ ...content, introParagraphs: e.target.value.split(/\n\n+/).filter(Boolean) })
                  }
                  className={`${inputCls} font-mono text-xs leading-relaxed`}
                />
              </div>
            </div>

            {/* 4. Homepage Feature Stats (3 Cards) */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 shadow-sm">
              <div className="border-b border-gray-100 pb-3">
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" /> 4. Homepage Feature Cards (3 Pillars)
                </h2>
                <p className="text-xs text-gray-500">The 3 feature cards displayed on the homepage.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[0, 1, 2].map((idx) => {
                  const stat = (content.stats && content.stats[idx]) || { title: "", text: "" };
                  return (
                    <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#e5243b]">
                        Card #{idx + 1}
                      </span>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-600 mb-1">Title</label>
                        <input
                          type="text"
                          value={stat.title || ""}
                          onChange={(e) => {
                            const newStats = [...(content.stats || [{}, {}, {}])];
                            newStats[idx] = { ...newStats[idx], title: e.target.value };
                            setContent({ ...content, stats: newStats });
                          }}
                          className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white"
                          placeholder={`Card ${idx + 1} Title`}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-600 mb-1">Description</label>
                        <textarea
                          rows={2}
                          value={stat.text || ""}
                          onChange={(e) => {
                            const newStats = [...(content.stats || [{}, {}, {}])];
                            newStats[idx] = { ...newStats[idx], text: e.target.value };
                            setContent({ ...content, stats: newStats });
                          }}
                          className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white"
                          placeholder={`Card ${idx + 1} Description`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 5. Homepage Why Choose */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 shadow-sm">
              <div className="border-b border-gray-100 pb-3">
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" /> 5. "Why Visit / Why Choose" Section
                </h2>
                <p className="text-xs text-gray-500">Secondary benefits and highlights section on the homepage.</p>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">"Why Choose" Title</label>
                <input
                  type="text"
                  value={content.whyTitle || ""}
                  data-testid="content-why-title"
                  onChange={(e) => setContent({ ...content, whyTitle: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  "Why Choose" Paragraphs (Separated by blank line)
                </label>
                <textarea
                  rows={5}
                  value={(content.whyParagraphs || []).join("\n\n")}
                  data-testid="content-why-paragraphs"
                  onChange={(e) =>
                    setContent({ ...content, whyParagraphs: e.target.value.split(/\n\n+/).filter(Boolean) })
                  }
                  className={`${inputCls} font-mono text-xs leading-relaxed`}
                />
              </div>
            </div>

            {/* 6. Top Rated & States Directory Pages Header */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 shadow-sm">
              <div className="border-b border-gray-100 pb-3">
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-500" /> 6. Directory Page Banners (Top Rated & States Index)
                </h2>
                <p className="text-xs text-gray-500">Custom headlines and subheaders for `/top-rated` and `/states`.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <span className="text-[11px] font-bold uppercase text-[#e5243b]">Top Rated Page (/top-rated)</span>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Page Headline</label>
                    <input
                      type="text"
                      value={content.topRatedTitle || "Top Rated Mini Golf Courses"}
                      onChange={(e) => setContent({ ...content, topRatedTitle: e.target.value })}
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Page Subtitle</label>
                    <textarea
                      rows={2}
                      value={content.topRatedSubtitle || "The highest-rated mini golf courses in Alabama and across the United States, ranked by real visitor ratings and verified review counts."}
                      onChange={(e) => setContent({ ...content, topRatedSubtitle: e.target.value })}
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white"
                    />
                  </div>
                </div>

                <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <span className="text-[11px] font-bold uppercase text-blue-600">States Directory Page (/states)</span>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Page Headline</label>
                    <input
                      type="text"
                      value={content.statesDirectoryTitle || "Browse All Cities by State"}
                      onChange={(e) => setContent({ ...content, statesDirectoryTitle: e.target.value })}
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Page Subtitle</label>
                    <textarea
                      rows={2}
                      value={content.statesDirectorySubtitle || "Explore mini golf courses in Alabama and all 50 states. Click a state to view its courses, or jump straight to a city."}
                      onChange={(e) => setContent({ ...content, statesDirectorySubtitle: e.target.value })}
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 7. Footer & Global Taglines */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 shadow-sm">
              <div className="border-b border-gray-100 pb-3">
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <Code className="w-4 h-4 text-gray-600" /> 7. Footer & Global Branding Copy
                </h2>
                <p className="text-xs text-gray-500">Taglines and copyright notices displayed across every page in the footer.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Footer Main Tagline</label>
                  <textarea
                    rows={2}
                    value={content.footerTagline || "Mini Golf Alabama (minigolfalabama.com) is your #1 guide to finding the best indoor and outdoor mini golf putt putt courses in Alabama and across the United States."}
                    onChange={(e) => setContent({ ...content, footerTagline: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Footer Secondary Description</label>
                  <textarea
                    rows={2}
                    value={content.footerDescription || "Discover top family fun parks, birthday venues, date night spots & glowing blacklight courses."}
                    onChange={(e) => setContent({ ...content, footerDescription: e.target.value })}
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">Copyright Notice</label>
                <input
                  type="text"
                  value={content.copyrightText || "Mini Golf Alabama (minigolfalabama.com). All rights reserved."}
                  onChange={(e) => setContent({ ...content, copyrightText: e.target.value })}
                  className={inputCls}
                />
              </div>
            </div>

            {/* Final Save Button */}
            <div className="flex justify-end pt-2">
              <button
                onClick={saveContent}
                className="inline-flex items-center gap-2 px-8 py-3 text-base font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-lg transition-all active:scale-[0.98]"
              >
                <Save className="w-5 h-5" /> Save All Site Content
              </button>
            </div>
          </div>
        )}

        {tab === "policies" && (
          <PolicyContentEditor
            privacyContent={privacyContent}
            termsContent={termsContent}
            onSavePrivacy={handleSavePrivacyContent}
            onSaveTerms={handleSaveTermsContent}
          />
        )}

        {tab === "schema" && (
          <SchemaSettingsEditor
            settings={seoSettings}
            onSave={handleSaveSeoSettings}
          />
        )}
      </div>

      {editing && (
        <CourseForm
          course={editing === "new" ? null : editing}
          states={STATES}
          allCourses={courses}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}

      {editingBlog && (
        <BlogForm
          blog={editingBlog === "new" ? null : editingBlog}
          onSave={handleSaveBlog}
          onCancel={() => setEditingBlog(null)}
        />
      )}

      {editingState && (
        <StateContentForm
          state={editingState.state}
          currentContent={editingState.currentContent}
          stateCourses={editingState.courses || []}
          onSave={handleSaveStateContent}
          onReset={handleResetStateContent}
          onClose={() => setEditingState(null)}
        />
      )}

      {editingCity && (
        <CityContentForm
          city={editingCity.city}
          currentContent={editingCity.currentContent}
          cityCourses={editingCity.city.courses || []}
          onSave={handleSaveCityContent}
          onReset={handleResetCityContent}
          onClose={() => setEditingCity(null)}
        />
      )}

      {showNewCityModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <h3 className="text-base font-bold text-gray-900">Add / Customize City Page</h3>
              <button
                onClick={() => setShowNewCityModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200/60"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateNewCity} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Select State *</label>
                <select
                  value={newCityState}
                  onChange={(e) => setNewCityState(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name} ({s.code.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">City Name *</label>
                <input
                  type="text"
                  required
                  value={newCityName}
                  onChange={(e) => setNewCityName(e.target.value)}
                  placeholder="e.g. Birmingham, Huntsville, Mobile"
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewCityModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors"
                >
                  Continue to Editor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Universal Section CSV Upload Modal */}
      <CsvUploadModal
        isOpen={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        sectionKey={csvModalSection}
        onImport={handleUniversalImport}
        allowModeSwitch={true}
      />
    </div>
  );
};

export default AdminDashboard;
